import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Timer, TriangleAlert } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { horas } from "../lib/tempo.js";
import { data as fData, plural } from "../lib/formato.js";

const hoje = () => new Date().toISOString().slice(0, 10);

const ACAO: Record<string, string> = {
  pagar: "vira hora extra a pagar",
  expurgar: "some do banco",
  descontar: "vira desconto em folha",
};

/**
 * Banco de horas em LOTES.
 *
 * A tela existe porque saldo não responde a pergunta que importa. "Você tem
 * 20 horas" não diz que 8 vencem em duas semanas sob política de pagamento e
 * 12 valem até janeiro, e é essa diferença que decide se a empresa paga a mais
 * ou a menos quando o prazo do acordo estoura.
 */
export function BancoDeHoras() {
  const [vinculoId, setVinculoId] = useState<string | null>(null);

  const pessoas = useQuery({
    queryKey: ["cadastro", "pessoas", "", false],
    queryFn: () => trpc.cadastro.pessoas.query({ busca: "", incluirDesligados: false, limite: 500 }),
  });
  const comVinculo = (pessoas.data ?? []).filter((p) => p.vinculoId);
  const alvo = vinculoId ?? comVinculo[0]?.vinculoId ?? null;

  const q = useQuery({
    queryKey: ["ponto", "banco", alvo],
    queryFn: () => trpc.jornada.bancoDeHoras.query({ vinculoId: alvo!, hoje: hoje(), diasDeAlerta: 45 }),
    enabled: !!alvo,
  });

  if (pessoas.isPending) {
    return <Pagina secao="Jornada" titulo="Banco de horas"><Carregando linhas={5} /></Pagina>;
  }
  if (comVinculo.length === 0) {
    return (
      <Pagina secao="Jornada" titulo="Banco de horas">
        <EmptyState titulo="Ninguém tem vínculo aberto"
                    descricao="Crédito e débito de banco de horas são por vínculo." />
      </Pagina>
    );
  }

  const s = q.data?.saldo;
  const vencidos = q.data?.vencidos ?? [];

  return (
    <Pagina
      secao="Jornada" titulo="Banco de horas"
      descricao="Cada crédito é um lote com validade própria. O débito consome em FIFO por vencimento, e o sistema sabe qual crédito venceu."
      acoes={
        <select className="campo h-8 w-auto text-xs" value={alvo ?? ""} aria-label="Pessoa"
                onChange={(e) => setVinculoId(e.target.value)}>
          {comVinculo.map((p) => (
            <option key={p.vinculoId!} value={p.vinculoId!}>{p.nome}</option>
          ))}
        </select>
      }
    >
      {q.isPending && <Carregando linhas={5} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

      {q.isSuccess && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="painel indicador p-4 pl-5">
              <span className="eyebrow">Saldo</span>
              <p className="num mt-2.5 text-[1.75rem] font-semibold leading-none">
                {horas(s?.minutos ?? 0)}
              </p>
            </div>
            <div className="painel indicador p-4 pl-5">
              <div className="flex items-center gap-1.5">
                {(s?.aVencer.length ?? 0) > 0 && <span aria-hidden className="sinal sinal-atencao" />}
                <span className="eyebrow">Vence em 45 dias</span>
              </div>
              <p className="num mt-2.5 text-[1.75rem] font-semibold leading-none">
                {horas(s?.aVencer.reduce((a, b) => a + b.minutos, 0) ?? 0)}
              </p>
            </div>
            <div className="painel indicador p-4 pl-5">
              <div className="flex items-center gap-1.5">
                {(s?.minutosVencidos ?? 0) > 0 && <span aria-hidden className="sinal sinal-critico" />}
                <span className="eyebrow">Já venceu</span>
              </div>
              <p className="num mt-2.5 text-[1.75rem] font-semibold leading-none">
                {horas(s?.minutosVencidos ?? 0)}
              </p>
            </div>
          </div>

          {vencidos.length > 0 && (
            <Cartao titulo="Lotes vencidos" subtitulo="Cada um com a política que o acordo definiu">
              <ul className="eop-lotes">
                {vencidos.map((v) => (
                  <li key={v.lote.id} data-vencido>
                    <TriangleAlert aria-hidden size={15} className="eop-lote-icone" />
                    <span className="min-w-0 flex-1">
                      <span className="eop-lote-nome">
                        {horas(v.minutos)} de {fData(v.lote.origemData)}
                      </span>
                      <span className="eop-lote-sub">
                        venceu em {fData(v.lote.venceEm)} · {ACAO[v.acao] ?? v.acao}
                      </span>
                    </span>
                    <span className="pilula">{v.acao}</span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          <Cartao titulo="Lotes"
                  subtitulo={q.data.lotes.length
                    ? `${plural(q.data.lotes.length, "lote", "lotes")}, do que vence antes para o que vence depois`
                    : "Nenhum crédito ainda"}>
            {q.data.lotes.length === 0 ? (
              <EmptyState
                titulo="Sem crédito no banco"
                descricao="O crédito nasce da apuração do dia: hora trabalhada além do previsto, quando a regra manda para o banco em vez de pagar."
              />
            ) : (
              <ul className="eop-lotes">
                {q.data.lotes.map((l) => {
                  const saldo = l.minutos - l.minutosConsumidos;
                  return (
                    <li key={l.id} data-liquidado={l.liquidado || undefined}>
                      <Timer aria-hidden size={15} className="eop-lote-icone" />
                      <span className="min-w-0 flex-1">
                        <span className="eop-lote-nome">
                          {horas(saldo)}
                          {l.minutosConsumidos > 0 && (
                            <span className="eop-lote-consumido">
                              {" "}de {horas(l.minutos)}, {horas(l.minutosConsumidos)} usado
                            </span>
                          )}
                        </span>
                        <span className="eop-lote-sub">
                          creditado em {fData(l.origemData)} · vence em {fData(l.venceEm)}
                        </span>
                      </span>
                      <span className="eop-lote-barra" aria-hidden>
                        <span style={{ width: `${(l.minutosConsumidos / l.minutos) * 100}%` }} />
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Cartao>

          {q.data.movimentos.length > 0 && (
            <Cartao titulo="Movimento" subtitulo="Append-only: correção é estorno mais lançamento novo">
              <ul className="eop-agenda">
                {q.data.movimentos.slice(0, 30).map((m) => (
                  <li key={m.id}>
                    <span className="num eop-agenda-dia">{fData(m.dataRef).slice(0, 5)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-agenda-nome">{m.tipo}</span>
                      {m.motivo && <span className="eop-agenda-base">{m.motivo}</span>}
                    </span>
                    <span className="num eop-agenda-quando"
                          data-tom={m.minutos < 0 ? "passou" : "longe"}>
                      {m.minutos > 0 ? "+" : ""}{horas(m.minutos)}
                    </span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}
        </>
      )}
    </Pagina>
  );
}
